/// <amd-module name="CodeCoverage/AllTab/AllTab" />
import * as React from "react";

import { ObservableValue, IObservableValue } from "VSS/Core/Observable";
import { VssComponent, Components, IVssComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { IFilter, IFilterState } from "VSSUI/Utilities/Filter";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { ZeroData } from "VSSUI/ZeroData";

import { IAllTabDataProviderService } from "CodeCoverage/AllTab/AllTabDataProviderService";
import { IAllTabViewService } from "CodeCoverage/AllTab/AllTabViewService";
import { CodeCoverageTree } from "CodeCoverage/AllTab/CodeCoverageTree";
import { CodeCoverageTreeItemProvider } from "CodeCoverage/AllTab/CodeCoverageTreeItemProvider";
import { ICodeCoverageTreeItem } from "CodeCoverage/AllTab/Contracts";
import * as Resources from "CodeCoverage/AllTab/Resources";
import { CoverageSummary } from "CodeCoverage/Clients/Coverage/Contracts/Coverage";
import { PerfScenarios, TelemetryEvents } from "CodeCoverage/ViewCommon/Constants";
import { publish } from "CodeCoverage/ViewCommon/Helpers";
import { RunViewArtifact } from "CodeCoverage/ViewCommon/RunViewArtifact";

export interface IAllTabProps {
    treeItemProvider?: CodeCoverageTreeItemProvider;
    runViewArtifact: RunViewArtifact;
    filter: IFilter;
}

export class AllTab extends VssComponent<IAllTabProps> {
    private itemProvider: CodeCoverageTreeItemProvider;
    private dataProviderService: IAllTabDataProviderService;
    private fileCodeCoverageItem: IObservableValue<ICodeCoverageTreeItem | undefined>;
    private isLoading: IObservableValue<boolean>;
    private filterInProgress: ObservableValue<boolean>;
    private allTabViewService: IAllTabViewService;
    private filteredItemProvider: CodeCoverageTreeItemProvider;

    constructor(props: IAllTabProps, context: IVssComponentContext) {
        super(props, context);

        this.itemProvider = this.props.treeItemProvider ? this.props.treeItemProvider : new CodeCoverageTreeItemProvider();
        this.fileCodeCoverageItem = new ObservableValue(undefined);

        this.dataProviderService = this.context.pageContext.getService<IAllTabDataProviderService>("IAllTabDataProviderService");
        const firstLevelItems = this.dataProviderService.getFirstLevelDirectoryCoverageSummaryData();
        if (firstLevelItems) {
            this.itemProvider.addFirstLevelItems(firstLevelItems);
        }

        this.isLoading = new ObservableValue(!firstLevelItems);
        this.filterInProgress = new ObservableValue(false);
        this.allTabViewService = this.context.pageContext.getService<IAllTabViewService>("IAllTabViewService");
        this.filteredItemProvider = new CodeCoverageTreeItemProvider();
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this.props.filter.subscribe(this.onFilterChanged);

        if (this.itemProvider.length === 0) {
            this.trackPromise(
                this.dataProviderService.getDirectoryCoverageSummaryDataAsync(
                    this.props.runViewArtifact.projectId,
                    this.props.runViewArtifact.buildId
                )
            ).promise.then(
                (items: CoverageSummary[] | undefined) => {
                    if (items) {
                        this.itemProvider.addFirstLevelItems(items);
                    }
                    this.isLoading.value = false;
                },
                () => {
                    this.isLoading.value = false;
                }
            );
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        this.props.filter.unsubscribe(this.onFilterChanged);
    }

    public render(): JSX.Element {
        return (
            <Observer isLoading={this.isLoading} filterInProgress={this.filterInProgress}>
                {(props: { isLoading: boolean; isNoDataScenario: boolean; filterInProgress: boolean }) => {
                    if (props.isLoading) {
                        return <Spinner label={Resources.LoadingSpinnerText} ariaLive="assertive" />;
                    } else if (this.itemProvider.length === 0) {
                        return (
                            <ZeroData
                                imagePath={getAssetLocation(
                                    this.context.pageContext,
                                    "ms.vss-codecoverage-ci-web/view-common/Content/no-results-image.png"
                                )}
                                imageAltText=""
                                primaryText={Resources.NoDataPrimaryText}
                                secondaryText={Resources.NoDataSecondaryText}
                            />
                        );
                    } else {
                        return (
                            <>
                                {props.filterInProgress ? (
                                    <CodeCoverageTree
                                        itemProvider={this.filteredItemProvider}
                                        onToggle={this.onFilterToggle}
                                        onFileRowClick={this.onFileRowClick}
                                    />
                                ) : (
                                    <CodeCoverageTree
                                        itemProvider={this.itemProvider}
                                        onToggle={this.onToggle}
                                        onFileRowClick={this.onFileRowClick}
                                    />
                                )}
                                <Observer fileCodeCoverageItem={this.fileCodeCoverageItem}>
                                    {(props: { fileCodeCoverageItem: ICodeCoverageTreeItem | undefined }) => {
                                        return props.fileCodeCoverageItem ? (
                                            <WrappedComponent
                                                dependencies={["ms.vss-codecoverage-ci-web.codecoverage-ci-file-coverage-pane"]}
                                                wrappedType="CC.CI.FileCoverageViewer"
                                                runViewArtifact={this.props.runViewArtifact}
                                                filePath={props.fileCodeCoverageItem.path}
                                                coveragePercentage={props.fileCodeCoverageItem.coveragePercentage}
                                                totalLines={props.fileCodeCoverageItem.totalLines}
                                                notCoveredLines={props.fileCodeCoverageItem.notCoveredLines}
                                                coveredLines={props.fileCodeCoverageItem.coveredLines}
                                                partiallyCoveredLines={props.fileCodeCoverageItem.partiallyCoveredLines}
                                                hideCoverageChangePercentage={true}
                                                onMount={this.onCodeCoverageFileViewerMount}
                                                onClose={this.onFileCoverageViewClose}
                                            />
                                        ) : null;
                                    }}
                                </Observer>
                            </>
                        );
                    }
                }}
            </Observer>
        );
    }

    private onCodeCoverageFileViewerMount = (): void => {
        publish(this.context.pageContext, TelemetryEvents.CodeCoverageFileDetailsViewed, this.props.runViewArtifact.buildId, { source: "AllTab" });
    };

    private onFileRowClick = (event: React.SyntheticEvent<HTMLElement>, treeItem: ICodeCoverageTreeItem): void => {
        this.fileCodeCoverageItem.value = treeItem;

        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.startScenario(PerfScenarios.FileCoverageDetails, true);
    };

    private onFileCoverageViewClose = (): void => {
        this.fileCodeCoverageItem.value = undefined;
    };

    private onFilterToggle = (treeItem: ITreeItemEx<ICodeCoverageTreeItem>): void => {
        const codeCoverageTreeItem = treeItem.underlyingItem;
        this.filteredItemProvider.toggle(codeCoverageTreeItem);
    };

    private onToggle = (treeItem: ITreeItemEx<ICodeCoverageTreeItem>): void => {
        // Handle toggle scenario
        const codeCoverageTreeItem = treeItem.underlyingItem;
        this.itemProvider.toggle(codeCoverageTreeItem);

        if (codeCoverageTreeItem.expanded) {
            const path = codeCoverageTreeItem.data.path;
            const childItems = this.itemProvider.getChildItems(path);
            if (!childItems || childItems.length === 0) {
                this.trackPromise(
                    this.dataProviderService.getDirectoryCoverageSummaryDataAsync(
                        this.props.runViewArtifact.projectId,
                        this.props.runViewArtifact.buildId,
                        path
                    )
                ).promise.then((items: CoverageSummary[]) => {
                    this.itemProvider.addChildItems(path, items);
                });
            }
        }
    };

    private onFilterChanged = (value: IFilterState): void => {
        const filterType = "all-tab-filter";
        const keywordState = value[filterType];
        if (keywordState) {
            this.filterItems(keywordState.value);
        }
    };

    private filterItems = (searchText: string): void => {
        if (searchText) {
            const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.startScenario(PerfScenarios.CodeCoverageAllTabFiltering, false);

            this.filterInProgress.value = true;
            const allItems = this.itemProvider.getAllItems();
            const result = this.allTabViewService.filterItems(searchText, allItems);
            this.filteredItemProvider.replaceAllItems(result);

            performanceService.endScenario(PerfScenarios.Area, PerfScenarios.CodeCoverageAllTabFiltering, false);
        } else {
            this.onFilterClear();
        }
    };

    private onFilterClear = (): void => {
        this.filterInProgress.value = false;
        this.props.filter.reset();
    };
}

Components.add("CodeCoverage.CI.AllTab", AllTab);
